import React, { useEffect, useState, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Stepper from '../../stepper';
import ErrorList from '../../errorList';
import { IVisitorAgreement, IVisitorDetailsFormValues } from '../types';
import DataPrivacyMessageStep from './dataPrivacyMessage';
import VisitorScanBadgeStep from './scanBadge';
import VisitorSearchStep, { IVisitorSearchResult } from './search';
import VisitorDetailsStep from './details';
import VisitorAgreementStep from './agreement';
import { useAuthentication } from '../../authentiction';
import {
  NotificationModal,
  INotificationModalProps,
  useModal,
} from '../../modals';
import {
  initialState,
  visitorQuestionnaireReducer,
  questionnaireInitialized,
  questionAnswered,
} from './questinnaire/questionnaireReducer';
import VisitorQuestionnaireSectionStep from './questinnaire';
import { usePreviousValue } from 'beautiful-react-hooks';
import mergeQuestionnaireIntoState from './questinnaire/mergeQuestionnaireIntoState';
import attemptVisitorSignIn from './attemptVisitorSignIn';
import { IApiError } from '../../../services';
import { IVisitorSignInStepperProps } from './types';

const initialVisitorDetails: IVisitorDetailsFormValues = {
  badgeNumber: '',
  firstNames: '',
  surname: '',
  company: '',
  host: '',
  carRegistration: '',
  emailAddress: '',
  mobile: '',
};

export const VisitorSignInStepper: React.FC<IVisitorSignInStepperProps> = ({
  receptionMode,
  returnUrl,
  badgeNumber,
}) => {
  const { pin, dataPrivacyMessage, visitorTypes, agreements, questionnaires } =
    useAuthentication();
  const { showModal, hideModal } = useModal();
  const history = useHistory();

  const [ajaxErrors, setAjaxErrors] = useState<Array<string>>([]);

  const [visitorTypeId, setVisitorTypeId] = useState<number>(
    visitorTypes!.allIds[0],
  );

  const [agreement, setAgreement] = useState<IVisitorAgreement | undefined>(
    undefined,
  );

  const [signature, setSignature] = useState<string>('');

  const [visitorDetails, setVisitorDetails] =
    useState<IVisitorDetailsFormValues>({
      ...initialVisitorDetails,
      ...(badgeNumber && { badgeNumber }),
    });

  const [isFinished, setIsFinished] = useState<boolean>(false);

  const [state, dispatch] = useReducer(
    visitorQuestionnaireReducer,
    initialState,
  );

  const previousVisitorTypeId = usePreviousValue(visitorTypeId);
  const previousIsFinished = usePreviousValue(isFinished);

  useEffect(() => {
    if (visitorTypeId !== previousVisitorTypeId && visitorTypes) {
      // set questionnaires and agreement based on visitor type
      let newState = initialState;

      if (questionnaires) {
        for (
          let index = 0;
          index < visitorTypes.byId[visitorTypeId].questionnaires.length;
          index++
        ) {
          newState = mergeQuestionnaireIntoState(
            newState,
            questionnaires.byId[
              visitorTypes.byId[visitorTypeId].questionnaires[index]
            ],
          );
        }
      }

      dispatch(questionnaireInitialized(newState));

      if (agreements) {
        setAgreement(
          agreements.byId[visitorTypes.byId[visitorTypeId].agreementId ?? 0],
        );
      }
    }
  }, [
    agreements,
    questionnaires,
    visitorTypes,
    visitorTypeId,
    previousVisitorTypeId,
  ]);

  useEffect(() => {
    if (isFinished && !previousIsFinished) {
      attemptVisitorSignIn(
        pin,
        visitorTypeId,
        visitorDetails,
        state,
        agreement ? agreement.id : null,
        signature,
      )
        .then(() => {
          if (receptionMode) {
            history.replace(returnUrl);
          } else {
            const notificationProps: INotificationModalProps = {
              title: 'Signed in',
              message: 'Thank you for signing in',
              icon: ['far', 'check-circle'],
              kind: 'success',
              timeout: 2000,
              onRequestClose: () => {
                hideModal();
                history.replace(returnUrl);
              },
            };

            showModal(NotificationModal, notificationProps);
          }
        })
        .catch((error: IApiError) => {
          if (error.errorNumber === 1011) {
            setAjaxErrors(['The badge has been deactivated.']);
          } else {
            setAjaxErrors(error.messages);
            setIsFinished(false);
          }
        });
    }
  }, [
    isFinished,
    previousIsFinished,
    pin,
    visitorTypeId,
    visitorDetails,
    state,
    agreement,
    signature,
    receptionMode,
    returnUrl,
    showModal,
    hideModal,
    history
  ]);

  const handleSearchComplete = (result: IVisitorSearchResult): void => {
    const { visitorTypeId, ...rest } = result;

    setVisitorDetails(Object.assign({}, visitorDetails, rest));

    if (visitorTypeId && visitorTypes!.allIds.indexOf(visitorTypeId) !== -1) {
      setVisitorTypeId(visitorTypeId);
    }
  };

  const handleFinish = (): void => {
    // changed to a state change because the visitor details
    // were not updated before the API call
    setIsFinished(true);
  };

  return (
    <Container>
      <ErrorList errors={ajaxErrors} />
      <Stepper cancelPath={returnUrl} onFinish={handleFinish}>
        {!receptionMode && dataPrivacyMessage?.length && (
          <Stepper.Step>
            <DataPrivacyMessageStep content={dataPrivacyMessage} />
          </Stepper.Step>
        )}
        {!receptionMode && (
          <Stepper.Step
            heading="Identification"
            subHeading="Please scan your visitor pass"
          >
            <VisitorScanBadgeStep
              onSuccess={(badgeNumber) =>
                setVisitorDetails(
                  Object.assign({}, visitorDetails, { badgeNumber }),
                )
              }
            />
          </Stepper.Step>
        )}
        <Stepper.Step
          heading="Email Address"
          subHeading="Please enter your email address"
        >
          <VisitorSearchStep
            initialValues={{
              emailAddress: visitorDetails.emailAddress,
            }}
            onSubmit={handleSearchComplete}
            onError={setAjaxErrors}
          />
        </Stepper.Step>
        <Stepper.Step
          heading="Personal Details"
          subHeading="Please enter your details to sign in"
        >
          <VisitorDetailsStep
            initialValues={visitorDetails}
            visitorTypeId={visitorTypeId}
            onVisitorTypeChange={setVisitorTypeId}
            onSubmit={setVisitorDetails}
          />
        </Stepper.Step>
        {state.sections.allIds.map((sectionId) => {
          const section = state.sections.byId[sectionId];
          const questions = section.questions.map(
            (questionId) => state.questions.byId[questionId],
          );

          return (
            <Stepper.Step key={sectionId} heading={section.heading}>
              <VisitorQuestionnaireSectionStep
                questions={questions}
                onAnswer={(questionId, value) =>
                  dispatch(questionAnswered(questionId, value))
                }
              />
            </Stepper.Step>
          );
        })}
        {agreement && !receptionMode && (
          <Stepper.Step>
            <VisitorAgreementStep
              {...agreement}
              onChangedDigitalSignature={setSignature}
            />
          </Stepper.Step>
        )}
      </Stepper>
    </Container>
  );
};
